import usePlacesAutocomplete, { GeocodeResult, getGeocode } from "use-places-autocomplete"
import { useEffect, useMemo, useState } from "react"
import { useApiIsLoaded } from "@vis.gl/react-google-maps"
import { GeoPlace, MapAutocompleteReturn, UseMapAutocompleteProps } from "./types"
import { useOnclickOutside } from "@/hooks/useOnclickOutside"

export const useGMAutocomplete = (
	props?: UseMapAutocompleteProps
): MapAutocompleteReturn => {
	const isLoaded = useApiIsLoaded()
	const { init, ready, value, suggestions, setValue, clearSuggestions } =
		usePlacesAutocomplete({
			requestOptions: {},
			initOnMount: false,
			defaultValue: props?.defaultValue?.toString(),
			debounce: 300,
		})
	const [selectedPlace, setSelectedPlace] = useState<GeoPlace | null>(null)

	useEffect(() => {
		if (selectedPlace) {
			props?.onSelectedPlaceChange(selectedPlace)
		}
	}, [selectedPlace])

	useEffect(() => {
		if (isLoaded) {
			init()
		}
	}, [isLoaded])

	const сontainerRef = useOnclickOutside(() => {
		clearSuggestions()
	})

	const selectByAddress = async (address: string) => {
		setValue(address, false)
		clearSuggestions()

		try {
			const results = await getGeocode({ address })
			const result = results[0]
			if (!result) {
				return
			}
			const mapped = mapGeocodeToGeoPlace(result)

			setSelectedPlace(mapped)
		} catch (error) {
			console.error("selectByAddress error: ", error)
		}
	}

	const selectByLatLon = async (lat: number, lon: number) => {
		try {
			const results = await getGeocode({ location: { lat, lng: lon } })
			const result = results[0]
			if (!result) {
				return
			}
			const mapped = mapGeocodeToGeoPlace(result)

			setSelectedPlace(mapped)
			setValue(mapped.displayName, false)
		} catch (error) {
			console.error("selectByLatLng error: ", error)
		}
	}

	return {
		suggestions: suggestions.data.map((x) => ({
			displayName: x.description,
			placeId: x.place_id,
			// TODO: fetch lat and lon if needed
			lat: 0,
			lon: 0,
		})),
		selectByAddress,
		selectByLatLon,
		clearSuggestions,
		handleSuggestClick: (place: GeoPlace) => {
			selectByAddress(place.displayName)
		},
		selectedPlace,
		input: {
			value,
			setValue: (val) => setValue(val, false),
			onChange: setValue,
		},
		сontainerRef,
		apiReady: ready,
	}
}

const mapGeocodeToGeoPlace = (item: GeocodeResult): GeoPlace => ({
	lat: item.geometry.location.lat(),
	lon: item.geometry.location.lng(),
	displayName: item.formatted_address,
	placeId: item.place_id,
})
